/**
 * How the microphone is held.
 *
 * Three ways, because three different rooms want three different things: an
 * open microphone among friends, hold-to-talk in a house with other people in
 * it, and a key that latches for anybody who cannot hold a key and aim at the
 * same time.
 *
 * The key is captured by pressing it rather than picked from a list of names,
 * so what you press is what it records - including the side buttons on a
 * mouse, which is what half of the people who want this are using.
 */

import { useEffect, useRef, useState } from 'react';
import { useSession } from '@/stores/session';
import { useVoice } from '@/stores/voice';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { Card, CardHead } from './ui';

type Mode = 'open' | 'hold' | 'toggle';

const MODES: { id: Mode; label: string; note: string; icon: string }[] = [
  { id: 'open', label: 'Always open', note: 'Everything your microphone hears is sent.', icon: 'mic' },
  { id: 'hold', label: 'Hold to talk', note: 'Silent until you hold the key.', icon: 'micOff' },
  { id: 'toggle', label: 'Key toggles it', note: 'Tap to open, tap to close.', icon: 'waves' }
];

/** What to call a key on screen. */
function keyName(key: string): string {
  if (!key) return 'none';
  if (key === ' ') return 'Space';
  if (key.startsWith('Mouse')) return key;
  return key.length === 1 ? key.toUpperCase() : key;
}

export function PushToTalk() {
  const settings = useSession((s) => s.settings);
  const saveSettings = useSession((s) => s.saveSettings);
  const voice = useVoice();
  const [listening, setListening] = useState(false);
  const held = useRef(false);

  const ptt = settings?.pushToTalk ?? { mode: 'open' as Mode, key: '' };

  /* Capturing the key. Escape gives up, and a mouse button counts. */
  useEffect(() => {
    if (!listening) return;
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Escape') { setListening(false); return; }
      void saveSettings({ pushToTalk: { ...ptt, key: e.key } });
      setListening(false);
    };
    const onMouse = (e: MouseEvent) => {
      if (e.button === 0) return;
      e.preventDefault();
      void saveSettings({ pushToTalk: { ...ptt, key: 'Mouse' + e.button } });
      setListening(false);
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('mousedown', onMouse, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('mousedown', onMouse, true);
    };
  }, [listening, ptt, saveSettings]);

  /*
   * The behaviour itself.
   *
   * Only while in a call, and only while the app has focus: a global hotkey
   * belongs to the desktop build's hotkey system, not to a settings panel.
   */
  useEffect(() => {
    if (ptt.mode === 'open' || !ptt.key || !voice.partyId) return;

    const matches = (e: KeyboardEvent | MouseEvent) =>
      ptt.key.startsWith('Mouse')
        ? 'button' in e && 'Mouse' + (e as MouseEvent).button === ptt.key
        : 'key' in e && (e as KeyboardEvent).key === ptt.key;

    const down = (e: KeyboardEvent | MouseEvent) => {
      if (!matches(e) || held.current) return;
      held.current = true;
      if (ptt.mode === 'hold') { if (voice.muted) voice.toggleMute(true); }
      else voice.toggleMute(true);
    };
    const up = (e: KeyboardEvent | MouseEvent) => {
      if (!matches(e)) return;
      held.current = false;
      if (ptt.mode === 'hold' && !voice.muted) voice.toggleMute(true);
    };

    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('mousedown', down);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('mousedown', down);
      window.removeEventListener('mouseup', up);
    };
  }, [ptt.mode, ptt.key, voice.partyId, voice.muted, voice]);

  /* Hold-to-talk starts closed, so joining a call never opens the mic. */
  useEffect(() => {
    if (ptt.mode === 'hold' && voice.partyId && !voice.muted && !held.current) voice.toggleMute(true);
    // Only when the call or the mode changes - not on every mute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ptt.mode, voice.partyId]);

  if (!settings) return null;

  return (
    <Card>
      <CardHead title="Microphone mode" />
      <div role="radiogroup" aria-label="Microphone mode" className="grid gap-2 sm:grid-cols-3">
        {MODES.map((m) => {
          const on = ptt.mode === m.id;
          return (
            <button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => void saveSettings({ pushToTalk: { ...ptt, mode: m.id } })}
              className={cn(
                'rounded-[calc(14px*var(--radius-mul,1))] p-3 text-left outline-none',
                'transition-[background-color,box-shadow] duration-[200ms] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-ink shadow-[inset_0_0_0_1px_var(--line-soft)] hover:bg-surface-3'
              )}
            >
              <Icon name={m.icon} size={18} />
              <span className="mt-1.5 block text-[15px] font-bold">{m.label}</span>
              <span className={cn('mt-0.5 block text-[12.5px]', on ? 'opacity-85' : 'text-mute')}>{m.note}</span>
            </button>
          );
        })}
      </div>

      {ptt.mode !== 'open' ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="text-[14px] text-dim">Key</span>
          <button
            type="button"
            onClick={() => setListening(true)}
            className={cn(
              'h-10 min-w-[130px] rounded-[calc(12px*var(--radius-mul,1))] px-4 font-mono text-[13.5px] font-bold outline-none',
              'transition-[background-color,box-shadow] duration-[160ms] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
              listening ? 'bg-accent-soft text-accent shadow-[inset_0_0_0_1px_var(--accent)]' : 'bg-surface-2 text-ink shadow-[inset_0_0_0_1px_var(--line)]'
            )}
          >
            {listening ? 'Press a key…' : keyName(ptt.key)}
          </button>
          {ptt.key ? (
            <button
              type="button"
              onClick={() => void saveSettings({ pushToTalk: { ...ptt, key: '' } })}
              className="text-[13px] font-semibold text-mute transition-colors hover:text-ink"
            >
              Clear
            </button>
          ) : null}
          <p className="m-0 w-full text-[12.5px] text-mute">
            {ptt.key
              ? ptt.mode === 'hold'
                ? 'Your microphone is closed until you hold ' + keyName(ptt.key) + '.'
                : keyName(ptt.key) + ' opens and closes your microphone.'
              : 'Pick a key. A mouse side button works too.'}
            {' It applies while Pugo has focus; the desktop app has a global hotkey in Keybinds.'}
          </p>
        </div>
      ) : null}
    </Card>
  );
}
